/**
 * The `Authorization` header every web API call carries.
 *
 * The API rejects any request without a valid bearer token, so each transport spreads
 * this into its fetch headers. The token comes from MSAL's cache and is refreshed
 * silently. When Entra needs the user back — an expired session or a new Conditional
 * Access prompt — this starts a redirect as well as failing the call, so the caller reports
 * the failure while the page navigates away.
 *
 * A call that cannot carry a token fails before it is sent, with the caller's own
 * `Unauthenticated` failure. That covers no signed-in account, a silent acquisition Entra
 * refuses (AADSTS65003 included) and a sign-in endpoint that cannot be reached. Each
 * transport acquires the header outside its fetch `try`, so none of these is reported as a
 * network drop, and the case submit does not retry it.
 *
 * Under `npm run dev` MSAL is never initialized and the API skips authentication locally,
 * so no header is sent.
 */
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiTokenRequest, msalInstance } from './authConfig';

declare const __AUTH_BYPASS__: boolean;

/**
 * Builds the bearer header, or throws `unauthenticated()` when no token can be had.
 *
 * @param unauthenticated Builds the calling feature's `Unauthenticated` failure.
 */
export async function apiAuthorizationHeaders(
  unauthenticated: () => unknown,
): Promise<Record<string, string>> {
  if (__AUTH_BYPASS__) {
    return {};
  }

  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) {
    throw unauthenticated();
  }

  try {
    const result = await msalInstance.acquireTokenSilent({ ...apiTokenRequest, account });
    return { Authorization: `Bearer ${result.accessToken}` };
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      try {
        await msalInstance.acquireTokenRedirect({ ...apiTokenRequest, account });
      } catch {
        // The call fails as unauthenticated whether or not the redirect could start.
      }
    }

    throw unauthenticated();
  }
}
